import { Helmet } from "react-helmet-async";

const SITE_NAME = "Global Property Mart";
const SITE_URL = "https://globalpropertymart.com";
const DEFAULT_DESC = "Buy, sell, rent, verify and finance properties across India. Verified brokers, advocates, loan agents and online auctions — headquartered in Nagpur.";
const DEFAULT_IMG = `${SITE_URL}/gpm-logo.png`;

/**
 * Drop this <Seo /> into the top of any page component to set
 * the document head: title, description, OG, Twitter cards, JSON-LD.
 */
export default function Seo({
    title,
    description = DEFAULT_DESC,
    path = "",
    image = DEFAULT_IMG,
    type = "website",
    jsonLd = null,
    noindex = false,
    keywords = "",
}) {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — One Platform For Every Property Need`;
    const url = `${SITE_URL}${path}`;
    const allJsonLd = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : (jsonLd ? [jsonLd] : []);

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={url} />
            {noindex && <meta name="robots" content="noindex,nofollow" />}

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={image} />
            <meta property="og:locale" content="en_IN" />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {/* JSON-LD structured data */}
            {allJsonLd.map((ld, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(ld)}
                </script>
            ))}
        </Helmet>
    );
}

// ---- JSON-LD builders ---------------------------------------------------

export function organizationJsonLd() {
    return {
        "@context": "https://schema.org",
        "@type": "RealEstateAgent",
        name: SITE_NAME,
        url: SITE_URL,
        logo: DEFAULT_IMG,
        description: DEFAULT_DESC,
        sameAs: [
            "https://www.facebook.com/globalpropertymart",
            "https://www.instagram.com/globalpropertymart",
            "https://www.linkedin.com/company/globalpropertymart",
        ],
        contactPoint: {
            "@type": "ContactPoint",
            contactType: "Customer Service",
            email: "globalpropertymart95@gmail.com",
            availableLanguage: ["English", "Hindi", "Marathi"],
        },
    };
}

export function propertyJsonLd(p) {
    if (!p) return null;
    const isRent = p.listing_type === "rent";
    const subtype = (p.subtype || p.property_type || "property").replace(/_/g, " ");
    return {
        "@context": "https://schema.org",
        "@type": isRent ? "RentAction" : "RealEstateListing",
        "@id": `${SITE_URL}/properties/${p.id}#listing`,
        name: p.title,
        description: p.description,
        url: `${SITE_URL}/properties/${p.id}`,
        image: (p.images || []).slice(0, 5).map((img) => img.startsWith("http") ? img : `${SITE_URL}${img.startsWith("/") ? img : `/${img}`}`),
        datePosted: p.created_at,
        ...(p.updated_at && { dateModified: p.updated_at }),
        offers: {
            "@type": "Offer",
            price: p.price,
            priceCurrency: "INR",
            availability: "https://schema.org/InStock",
            url: `${SITE_URL}/properties/${p.id}`,
            seller: { "@id": `${SITE_URL}/#organization` },
        },
        ...(p.area_sqft && { floorSize: { "@type": "QuantitativeValue", value: p.area_sqft, unitCode: "FTK" } }),
        ...(p.bhk && { numberOfRooms: p.bhk }),
        ...(p.bathrooms && { numberOfBathroomsTotal: p.bathrooms }),
        ...(p.total_floors && { numberOfFloors: p.total_floors }),
        ...(p.facing && { additionalProperty: [{ "@type": "PropertyValue", name: "Facing", value: p.facing }] }),
        category: subtype,
        address: {
            "@type": "PostalAddress",
            streetAddress: p.address || p.locality || "",
            addressLocality: p.city,
            addressRegion: p.state,
            ...(p.pincode && { postalCode: p.pincode }),
            addressCountry: "IN",
        },
        ...((p.latitude && p.longitude) && {
            geo: { "@type": "GeoCoordinates", latitude: p.latitude, longitude: p.longitude },
        }),
        ...(p.amenities && p.amenities.length > 0 && {
            amenityFeature: p.amenities.map((a) => ({ "@type": "LocationFeatureSpecification", name: a, value: true })),
        }),
        ...(p.rating && p.reviews_count > 0 && {
            aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: p.rating,
                reviewCount: p.reviews_count,
                bestRating: 5,
                worstRating: 1,
            },
        }),
        broker: { "@id": `${SITE_URL}/#organization` },
    };
}

/**
 * ItemList JSON-LD — for search-result / listing pages.
 * items: array of properties (uses id, title, price, city, images).
 */
export function itemListJsonLd(items, { name = "Properties", pathPrefix = "/properties/" } = {}) {
    if (!items || items.length === 0) return null;
    return {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name,
        numberOfItems: items.length,
        itemListElement: items.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}${pathPrefix}${p.id}`,
            name: p.title,
            image: p.images?.[0]
                ? (p.images[0].startsWith("http") ? p.images[0] : `${SITE_URL}${p.images[0].startsWith("/") ? p.images[0] : `/${p.images[0]}`}`)
                : undefined,
        })),
    };
}

/**
 * FAQPage JSON-LD — Google rich result eligible.
 * items: [{ q: "question", a: "answer" }]
 */
export function faqJsonLd(items) {
    if (!items || items.length === 0) return null;
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((it) => ({
            "@type": "Question",
            name: it.q,
            acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
    };
}

/**
 * Article / BlogPosting JSON-LD (for guides & long-form content).
 */
export function articleJsonLd({ title, description, image, slug, datePublished, dateModified, author = "Global Property Mart" }) {
    return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: title,
        description,
        image: image ? [image.startsWith("http") ? image : `${SITE_URL}${image}`] : undefined,
        url: `${SITE_URL}/guides/${slug}`,
        datePublished,
        dateModified: dateModified || datePublished,
        author: { "@type": "Organization", name: author, url: SITE_URL },
        publisher: { "@id": `${SITE_URL}/#organization` },
        mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/guides/${slug}` },
    };
}

/**
 * LocalBusiness / Place JSON-LD — for city / area landing pages.
 */
export function localBusinessJsonLd({ name, city, state = "Maharashtra", description, url, image = DEFAULT_IMG }) {
    return {
        "@context": "https://schema.org",
        "@type": "RealEstateAgent",
        name,
        description,
        url: url ? `${SITE_URL}${url}` : SITE_URL,
        image,
        parentOrganization: { "@id": `${SITE_URL}/#organization` },
        areaServed: { "@type": "City", name: city, containedInPlace: { "@type": "State", name: state } },
        address: {
            "@type": "PostalAddress",
            addressLocality: city,
            addressRegion: state,
            addressCountry: "IN",
        },
    };
}


// Pre-built Maharashtra/Nagpur location coverage for SEO
export const NAGPUR_AREAS = [
    { slug: "wardha-road", name: "Wardha Road", desc: "fastest-growing real estate corridor with premium gated communities" },
    { slug: "mihan", name: "MIHAN", desc: "SEZ & IT hub with strong infrastructure & investment potential" },
    { slug: "mohgaon", name: "Mohgaon", desc: "emerging residential township area with NA plots & farmland" },
    { slug: "beltarodi", name: "Beltarodi", desc: "premium gated community plots & villas near outer ring road" },
    { slug: "jamtha", name: "Jamtha", desc: "investment-friendly belt with affordable NA & layout plots" },
    { slug: "manish-nagar", name: "Manish Nagar", desc: "established residential locality near central Nagpur" },
    { slug: "hingna", name: "Hingna", desc: "industrial-residential mixed-use belt with affordable plots" },
    { slug: "samruddhi-mahamarg", name: "Samruddhi Mahamarg", desc: "Mumbai-Nagpur expressway corridor with high appreciation NA plots" },
    { slug: "nagpur-metro", name: "Nagpur Metro Corridor", desc: "connectivity-rich residential & commercial properties along metro line" },
    { slug: "airport", name: "Near Nagpur Airport", desc: "premium plots & commercial properties near Dr. Babasaheb Ambedkar International Airport" },
];

export function breadcrumbJsonLd(items) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.name,
            item: `${SITE_URL}${it.path || it.url || ""}`,
        })),
    };
}
